/**
 * UATAudioRecorder.jsx — Browser audio recorder for UAT voice input.
 *
 * Uses the browser's native MediaRecorder API (no additional dependencies).
 * Records in webm/opus format (widest browser support). Sends audio to the
 * AcademiaOS backend proxy — never directly to Sarvam.
 *
 * Lifecycle:
 *   IDLE → REQUESTING_MIC → RECORDING → PROCESSING → DONE / ERROR
 *
 * The tester can review and edit both transcripts before the parent form
 * uses them. Original transcript is always the authoritative record.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto-detect" },
  { value: "te-IN", label: "Telugu (తెలుగు)" },
  { value: "hi-IN", label: "Hindi (हिंदी)" },
  { value: "en-IN", label: "English (Indian)" },
  { value: "kn-IN", label: "Kannada (ಕನ್ನಡ)" },
];

const MAX_RECORD_MS = 30_000; // 30-second hard limit (Saaras v4 REST constraint)

export function UATAudioRecorder({ onTranscriptReady, token }) {
  const [phase, setPhase] = useState("IDLE"); // IDLE|REQUESTING_MIC|RECORDING|PROCESSING|DONE|ERROR
  const [language, setLanguage] = useState("auto");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [native, setNative] = useState("");
  const [english, setEnglish] = useState("");
  const [detectedLang, setDetectedLang] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
  }, []);

  // Auto-stop at 30 s
  useEffect(() => {
    if (phase === "RECORDING" && elapsed >= MAX_RECORD_MS / 1000) {
      stopRecording();
    }
  }, [elapsed, phase, stopRecording]);

  const startRecording = async () => {
    setError(null);
    setPhase("REQUESTING_MIC");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer webm/opus; fall back to browser default
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => processAudio(mimeType || "audio/webm");

      recorderRef.current = recorder;
      recorder.start(250); // collect chunks every 250 ms
      setPhase("RECORDING");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      setPhase("ERROR");
      setError(
        err.name === "NotAllowedError"
          ? "Microphone permission denied. Please allow microphone access and try again."
          : `Microphone error: ${err.message}`
      );
    }
  };

  const processAudio = async (mimeType) => {
    setPhase("PROCESSING");
    const blob = new Blob(chunksRef.current, { type: mimeType });
    const formData = new FormData();
    formData.append("file", blob, "recording.webm");
    formData.append("language_code", language);

    try {
      const res = await fetch(`${API}/api/language/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail?.message || "Transcription failed.");
      }

      const data = await res.json();
      const nativeText = data.native?.transcript || "";
      const englishText = data.english?.transcript || "";
      const lang = data.native?.language_code || null;

      setNative(nativeText);
      setEnglish(englishText);
      setDetectedLang(lang);
      setPhase("DONE");

      // Notify parent with initial values — parent should allow editing
      onTranscriptReady?.({
        original_text: nativeText,
        original_language: lang || language !== "auto" ? language : null,
        normalized_english_text: englishText,
        translation_provider: "sarvam",
        translation_model: "saaras:v4",
      });
    } catch (err) {
      setPhase("ERROR");
      setError(err.message);
    }
  };

  const reset = () => {
    stopRecording();
    setPhase("IDLE");
    setElapsed(0);
    setError(null);
    setNative("");
    setEnglish("");
    setDetectedLang(null);
  };

  return (
    <div className="space-y-4" data-testid="uat-audio-recorder">
      {/* Language selector */}
      {phase === "IDLE" && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            Language
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="uat-language-select"
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-muted-foreground mt-1">
            Speak naturally — Telugu, Hindi, English or code-mixed speech are all supported.
          </p>
        </div>
      )}

      {/* Record / Stop button */}
      <div className="flex items-center gap-3">
        {phase === "IDLE" && (
          <button
            type="button"
            onClick={startRecording}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm font-medium hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors"
            data-testid="uat-record-btn"
          >
            <Mic className="h-4 w-4" />
            Start Recording
          </button>
        )}
        {phase === "REQUESTING_MIC" && (
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Requesting microphone access…
          </p>
        )}
        {phase === "RECORDING" && (
          <>
            <button
              type="button"
              onClick={stopRecording}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
              data-testid="uat-stop-btn"
            >
              <Square className="h-4 w-4 fill-white" />
              Stop
            </button>
            <span className="text-sm font-mono text-red-600 dark:text-red-400 animate-pulse">
              ● {elapsed}s / 30s
            </span>
          </>
        )}
        {phase === "PROCESSING" && (
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Transcribing your speech…
          </p>
        )}
        {(phase === "DONE" || phase === "ERROR") && (
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded-lg px-3 py-1.5"
            data-testid="uat-record-again-btn"
          >
            <Mic className="h-3.5 w-3.5" />
            Record again
          </button>
        )}
      </div>

      {/* Error */}
      {phase === "ERROR" && error && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30">
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Transcript review — tester edits before submitting */}
      {phase === "DONE" && (
        <div className="space-y-3" data-testid="uat-transcript-review">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              Transcription complete
              {detectedLang && ` — detected: ${detectedLang}`}
            </p>
          </div>

          {/* Native transcript — authoritative source */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">
              Original transcript{" "}
              <span className="font-normal text-muted-foreground">
                (authoritative — edit if needed)
              </span>
            </label>
            <textarea
              value={native}
              onChange={(e) => {
                setNative(e.target.value);
                onTranscriptReady?.({
                  original_text: e.target.value,
                  original_language: detectedLang || (language !== "auto" ? language : null),
                  normalized_english_text: english,
                  translation_provider: "sarvam",
                  translation_model: "saaras:v4",
                });
              }}
              rows={3}
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="uat-native-transcript"
            />
          </div>

          {/* English interpretation — working copy */}
          {english && (
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                English interpretation{" "}
                <span className="font-normal text-muted-foreground">
                  (working copy — not the authority)
                </span>
              </label>
              <textarea
                value={english}
                onChange={(e) => {
                  setEnglish(e.target.value);
                  onTranscriptReady?.({
                    original_text: native,
                    original_language: detectedLang || (language !== "auto" ? language : null),
                    normalized_english_text: e.target.value,
                    translation_provider: "sarvam",
                    translation_model: "saaras:v4",
                  });
                }}
                rows={3}
                className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="uat-english-transcript"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
